import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { XMarkIcon, ArrowsPointingOutIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

const NODE_CANVAS_MODE = () => 'replace';
const MAX_BOOKMARKS = 500;

const DEFAULT_COLOR = '#93c5fd';
const SELECTED_COLOR = '#4f46e5';
const NEIGHBOR_COLOR = '#60a5fa';
const DIMMED_COLOR = '#d1d5db';

function getDomain(url) {
  try { return new URL(url).hostname.replace(/^www\./, ''); }
  catch { return ''; }
}

const BookmarkGraph = ({ bookmarks, isDark = false }) => {
  const [selectedNode, setSelectedNode] = useState(null);
  const [minShared, setMinShared] = useState(1);
  const [containerWidth, setContainerWidth] = useState(600);
  const containerRef = useRef(null);
  const graphRef = useRef(null);
  const engineSettledRef = useRef(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(entries => {
      setContainerWidth(entries[0].contentRect.width);
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  const truncated = useMemo(() => bookmarks.slice(0, MAX_BOOKMARKS), [bookmarks]);

  // Build nodes and edges — edges exist when two bookmarks share at least minShared tags
  const graphData = useMemo(() => {
    if (!truncated.length) return { nodes: [], links: [] };

    const nodes = truncated.map(b => ({
      id: b.bookmarkId,
      title: b.title || getDomain(b.url) || b.bookmarkId,
      url: b.url || '',
      domain: getDomain(b.url || ''),
      tags: b.tags || [],
    }));

    const links = [];
    for (let i = 0; i < truncated.length; i++) {
      const tagsI = new Set(truncated[i].tags || []);
      if (tagsI.size === 0) continue;
      for (let j = i + 1; j < truncated.length; j++) {
        const shared = (truncated[j].tags || []).filter(t => tagsI.has(t)).length;
        if (shared >= minShared) {
          links.push({ source: truncated[i].bookmarkId, target: truncated[j].bookmarkId, value: shared });
        }
      }
    }

    return { nodes, links };
  }, [truncated, minShared]);

  // Separate connected nodes from isolated ones (isolated have no edges)
  const { connectedGraphData, isolatedNodes } = useMemo(() => {
    const connectedIds = new Set(graphData.links.flatMap(l => [
      typeof l.source === 'object' ? l.source.id : l.source,
      typeof l.target === 'object' ? l.target.id : l.target,
    ]));
    return {
      connectedGraphData: {
        nodes: graphData.nodes.filter(n => connectedIds.has(n.id)),
        links: graphData.links,
      },
      isolatedNodes: graphData.nodes.filter(n => !connectedIds.has(n.id)),
    };
  }, [graphData]);

  const { neighborSet, neighborLinks } = useMemo(() => {
    if (!selectedNode) return { neighborSet: new Set(), neighborLinks: new Set() };
    const nSet = new Set();
    const lSet = new Set();
    graphData.links.forEach(link => {
      const srcId = typeof link.source === 'object' ? link.source.id : link.source;
      const tgtId = typeof link.target === 'object' ? link.target.id : link.target;
      if (srcId === selectedNode.id) { nSet.add(tgtId); lSet.add(link); }
      if (tgtId === selectedNode.id) { nSet.add(srcId); lSet.add(link); }
    });
    return { neighborSet: nSet, neighborLinks: lSet };
  }, [selectedNode, graphData.links]);

  // Neighbors sorted by shared-tag count for the info panel
  const neighbors = useMemo(() => {
    if (!selectedNode) return [];
    const result = [];
    graphData.links.forEach(link => {
      const srcId = typeof link.source === 'object' ? link.source.id : link.source;
      const tgtId = typeof link.target === 'object' ? link.target.id : link.target;
      const otherId = srcId === selectedNode.id ? tgtId : tgtId === selectedNode.id ? srcId : null;
      if (!otherId) return;
      const other = graphData.nodes.find(n => n.id === otherId);
      if (other) result.push({ node: other, shared: link.value });
    });
    return result.sort((a, b) => b.shared - a.shared).slice(0, 6);
  }, [selectedNode, graphData]);

  const getNodeVal = useCallback(node => Math.max(3, (node.tags.length || 1) * 2), []);
  const getLinkWidth = useCallback(link => Math.max(1, link.value), []);

  const getNodeColor = useCallback(node => {
    if (!selectedNode) return DEFAULT_COLOR;
    if (node.id === selectedNode.id) return SELECTED_COLOR;
    if (neighborSet.has(node.id)) return NEIGHBOR_COLOR;
    return DIMMED_COLOR;
  }, [selectedNode, neighborSet]);

  const getLinkColor = useCallback(link => {
    if (!selectedNode) return '#bfdbfe';
    if (neighborLinks.has(link)) return '#818cf8';
    return '#e5e7eb';
  }, [selectedNode, neighborLinks]);

  const drawNode = useCallback((node, ctx, globalScale) => {
    const color = getNodeColor(node);
    const radius = Math.max(3, (node.tags.length || 1) * 2);
    ctx.beginPath();
    ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();

    if (globalScale > 1.8 || (selectedNode && node.id === selectedNode.id)) {
      const label = node.title.length > 28 ? node.title.slice(0, 26) + '\u2026' : node.title;
      const fontSize = Math.max(7, 10 / globalScale);
      ctx.font = `${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = isDark ? '#e2e8f0' : '#1e293b';
      ctx.fillText(label, node.x, node.y + radius + 2);
    }
  }, [getNodeColor, selectedNode, isDark]);

  const handleNodeClick = useCallback(node => {
    setSelectedNode(prev => prev?.id === node.id ? null : node);
  }, []);

  const handleBackgroundClick = useCallback(() => setSelectedNode(null), []);

  const handleEngineStop = useCallback(() => {
    engineSettledRef.current = true;
    graphRef.current?.zoomToFit(400, 40);
  }, []);

  const handleFitView = useCallback(() => {
    if (!graphRef.current) return;
    graphRef.current.zoom(1, 0);
    graphRef.current.centerAt(0, 0, 0);
    setTimeout(() => graphRef.current?.zoomToFit(400, 40), 50);
  }, []);

  const handleReheat = useCallback(() => {
    if (!graphRef.current) return;
    engineSettledRef.current = false;
    graphRef.current.d3ReheatSimulation();
  }, []);

  // Configure D3 forces for better spread whenever data changes
  useEffect(() => {
    const g = graphRef.current;
    if (!g) return;
    // Increase repulsion so nodes push apart (default is -30)
    g.d3Force('charge')?.strength(-250);
    // Increase link resting distance so connected nodes aren't pulled too close
    g.d3Force('link')?.distance(100).strength(0.3);
    g.d3ReheatSimulation();
  }, [connectedGraphData]);

  if (graphData.nodes.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center text-gray-500 dark:text-gray-400">
        No bookmarks to display.
      </div>
    );
  }

  if (connectedGraphData.nodes.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center text-gray-500 dark:text-gray-400">
        <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">No bookmark connections yet.</p>
        <p className="text-sm">Bookmarks need at least one common tag to show a relationship.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {bookmarks.length > MAX_BOOKMARKS && (
        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-400 text-sm rounded-lg px-4 py-2">
          Showing first {MAX_BOOKMARKS} of {bookmarks.length} bookmarks.
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-4 md:items-start">
        {/* Stats panel */}
        <div className="w-full md:w-44 md:flex-shrink-0 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Overview</p>
          <div className="space-y-3">
            <div>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{connectedGraphData.nodes.length}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">connected</p>
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{graphData.links.length}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">connections</p>
            </div>
            {isolatedNodes.length > 0 && (
              <div>
                <p className="text-xl font-bold text-gray-400 dark:text-gray-500">{isolatedNodes.length}</p>
                <p className="text-xs text-gray-400 dark:text-gray-500">no connections</p>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-4 pt-3 border-t border-gray-100 dark:border-gray-700 leading-relaxed">
            Node size = tag count<br />
            Edge weight = shared tags
          </p>
        </div>

        {/* Graph + controls */}
        <div className="flex-1 min-w-0 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <label className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                Min. shared tags:
              </label>
              <input
                type="range"
                min={1}
                max={5}
                value={minShared}
                onChange={e => setMinShared(Number(e.target.value))}
                className="w-24 accent-indigo-600"
              />
              <span className="text-xs font-medium text-indigo-700 dark:text-indigo-400 w-4 text-center">{minShared}</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleReheat}
                className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-md shadow-sm transition-colors"
                title="Re-run layout simulation"
              >
                Re-layout
              </button>
              <button
                onClick={handleFitView}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 bg-white hover:bg-gray-50 border border-gray-200 rounded-md shadow-sm transition-colors"
                title="Fit all nodes into view"
              >
                <ArrowsPointingOutIcon className="h-3.5 w-3.5" />
                Fit to view
              </button>
            </div>
          </div>

          <div ref={containerRef} className="bg-white dark:bg-gray-900 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700">
            <ForceGraph2D
              ref={graphRef}
              graphData={connectedGraphData}
              nodeVal={getNodeVal}
              nodeColor={getNodeColor}
              linkWidth={getLinkWidth}
              linkColor={getLinkColor}
              onNodeClick={handleNodeClick}
              onBackgroundClick={handleBackgroundClick}
              nodeCanvasObject={drawNode}
              nodeCanvasObjectMode={NODE_CANVAS_MODE}
              onEngineStop={handleEngineStop}
              enableNodeDrag={true}
              warmupTicks={0}
              cooldownTime={5000}
              d3AlphaMin={0.001}
              minZoom={0.05}
              maxZoom={8}
              width={containerWidth}
              height={520}
              d3VelocityDecay={0.2}
              backgroundColor={isDark ? '#111827' : '#ffffff'}
            />
          </div>

          {selectedNode && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-indigo-200 dark:border-indigo-700 p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">{selectedNode.title}</p>
                  {selectedNode.domain && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{selectedNode.domain}</p>
                  )}

                  {selectedNode.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {selectedNode.tags.slice(0, 8).map(t => (
                        <span key={t} className="text-xs bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded-full">{t}</span>
                      ))}
                      {selectedNode.tags.length > 8 && (
                        <span className="text-xs text-gray-400 dark:text-gray-500">+{selectedNode.tags.length - 8} more</span>
                      )}
                    </div>
                  )}

                  {neighbors.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">Similar bookmarks:</p>
                      <div className="space-y-1">
                        {neighbors.map(({ node, shared }) => (
                          <button
                            key={node.id}
                            onClick={() => setSelectedNode(node)}
                            className="w-full text-left flex items-center justify-between gap-2 text-xs text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 py-0.5"
                          >
                            <span className="truncate">{node.title}</span>
                            <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">{shared} tag{shared !== 1 ? 's' : ''}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedNode.url && (
                    <a
                      href={selectedNode.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-1 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium transition-colors"
                    >
                      Open <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>

                <button
                  onClick={() => setSelectedNode(null)}
                  className="ml-3 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
                  aria-label="Dismiss"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {isolatedNodes.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-3">
            Bookmarks with no connections
          </p>
          <div className="flex flex-wrap gap-2">
            {isolatedNodes.map(node => (
              <a
                key={node.id}
                href={node.url}
                target="_blank"
                rel="noopener noreferrer"
                title={node.title}
                className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 hover:text-indigo-700 dark:hover:text-indigo-400 px-3 py-1 rounded-full text-sm transition-colors max-w-xs truncate"
              >
                {node.domain || node.title}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookmarkGraph;
